import { useEffect, useRef, useState } from 'react';

import { AnalyticsContext } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Flex, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { type NextBestAction } from '@post-office/confluence-next-best-action';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import type { ClickedElementAttributes } from '@post-office/shared-contracts/message-instance-event';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { use3pConnectorResources } from '@atlassian/confluence-account-setup/use3p-connector-resources';
import { ThirdPartyConfigs, ProductKeys3P } from '@atlassian/rovo-connectors-config';
import type { RovoLogo } from '@atlassian/rovo-connectors-config/icons';

import { DEFAULT_FALLBACK_APPS } from '../../../../../implicit-message/defaults';

import { Connect3pToolsModal, type AnimationApp, type Department } from '../connect-3p-tools-modal';

import { type JSX } from 'react/jsx-runtime';

/**
 * The set of third-party apps the side-nav nudge can promote.
 *
 * Kept intentionally narrow: only includes apps that appear in the upstream
 * `unconnectedApps` data sources (TAP traits and hardcoded defaults) and for
 * which we have a `ProductKeys3P` entry. If an upstream source returns an
 * app-id that isn't in this union, the implicit-message-creator drops it
 * silently before persisting `unconnectedApps`.
 */
export type ThirdPartyApp =
	| 'google-drive'
	| 'slack'
	| 'figma'
	| 'github'
	| 'onedrive'
	| 'sharepoint';

/** Maps our ThirdPartyApp identifiers to ProductKeys3P and display names */
const APP_CONFIG: Record<
	ThirdPartyApp,
	{ displayName: string; productKey: (typeof ProductKeys3P)[keyof typeof ProductKeys3P] }
> = {
	'google-drive': { displayName: 'Google Drive', productKey: ProductKeys3P.GoogleDrive },
	slack: { displayName: 'Slack', productKey: ProductKeys3P.Slack },
	figma: { displayName: 'Figma', productKey: ProductKeys3P.Figma },
	github: { displayName: 'GitHub', productKey: ProductKeys3P.Github },
	onedrive: { displayName: 'OneDrive', productKey: ProductKeys3P.OneDrive },
	sharepoint: { displayName: 'SharePoint', productKey: ProductKeys3P.Sharepoint },
};

const i18n = defineMessages({
	headingLabel: {
		id: 'post-office.confluence-connect-3p-tools-nudge.heading',
		defaultMessage: 'See more at a glance',
		description: 'Heading for the Confluence 3p connect your tools side nav nudge.',
	},
	multiAppBodyLabel: {
		id: 'post-office.confluence-connect-3p-tools-nudge.multi-app-body',
		defaultMessage: 'Connect your apps to embed rich links in this space.',
		description: 'Body text for the Confluence 3p connect your tools side nav nudge.',
	},
	singleAppBodyLabel: {
		id: 'post-office.confluence-connect-3p-tools-nudge.single-app-body',
		defaultMessage: 'Connect {appName} to embed rich links in this space.',
		description: 'Body text for the Confluence 3p connect your tools side nav nudge.',
	},
	callToActionForMultiAppLabel: {
		id: 'post-office.confluence-connect-3p-tools-nudge.multi-app.call.to.action',
		defaultMessage: 'Connect apps',
		description: 'Call to action button on the Confluence 3p connect your tools side nav nudge.',
	},
	callToActionForSingleAppLabel: {
		id: 'post-office.confluence-connect-3p-tools-nudge.single-app.call.to.action',
		defaultMessage: 'Connect {appName}',
		description: 'Call to action button on the Confluence 3p connect your tools side nav nudge.',
	},
	dismissLabel: {
		id: 'post-office.confluence-connect-3p-tools-nudge.dismiss',
		defaultMessage: 'Dismiss',
		description: 'Dismiss button on the Confluence 3p connect your tools side nav nudge.',
	},
});

const styles = cssMap({
	root: {
		position: 'relative',

		flexGrow: 1,

		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.large'),
	},

	primaryContentContainer: {
		flexGrow: 1,

		padding: token('space.200'),
	},

	callToActionContainer: {
		cursor: 'pointer',
		padding: '0',
		backgroundColor: 'transparent',

		'&:hover': {
			textDecoration: 'underline',
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
	},

	iconsColumn: {
		flex: '0 0 auto',
		paddingLeft: token('space.200'),
		paddingTop: token('space.200'),
	},

	iconWrapper: {
		width: '24px',
		height: '24px',
		borderRadius: token('radius.small'),
		overflow: 'hidden',
	},

	iconWrapperOverlap: {
		width: '24px',
		height: '24px',
		borderRadius: token('radius.small'),
		overflow: 'hidden',
		marginLeft: token('space.negative.100'),
	},

	closeButtonContainer: {
		position: 'absolute',
		top: token('space.100'),
		right: token('space.100'),
	},
});

type Connect3pToolsMessageInstanceClickedParams = {
	actionType: 'open' | 'dismiss';
	clickedElement: ClickedElementAttributes;
};

type TeammatesProps =
	| { numTeammates?: undefined; appName?: undefined }
	| { numTeammates: number; appName: string };

export type Connect3pToolsProps = {
	action: NextBestAction;
	unconnectedApps?: ThirdPartyApp[];
	cohort?: string;
	/**
	 * Atlassian cloudId for the current site. Used to look up the user's
	 * actual 3p connector state via `use3pConnectorResources` so the banner
	 * can suppress recommended apps the user has already connected. May be
	 * `undefined` for Storybook / standalone usages — in that case the hook
	 * still runs but with an empty cloudId, which initialises
	 * `isLoading: true` and falls back to the hook's unauthenticated
	 * connector list. The component therefore renders `null` during the
	 * initial async tick even in Storybook; this is a deliberate trade-off
	 * for keeping a single source of truth on connection state in production.
	 */
	cloudId?: string;
	/**
	 * Atlassian accountId of the viewer. Used as the per-user cache key in
	 * `use3pConnectorResources`. Optional for Storybook / standalone usages.
	 */
	userId?: string;
	/**
	 * The user's top 3 recommended apps in priority order. Used to select the
	 * animation SVG — the first app with a dedicated SVG wins. Falls back to
	 * department-based selection when none of the top apps have a dedicated SVG.
	 */
	topApps?: AnimationApp[];
	/**
	 * The user's department. Used as fallback to select the animation SVG when
	 * none of the top apps have a dedicated animation.
	 */
	department?: Department;
	onPrimaryActionClick?: (nextBestActionType: NextBestAction) => string | void | null;
	onDismissClick?: () => void;
} & TeammatesProps;

// Single source of truth: derived from `HARDCODED_DEFAULTS.na` in the cascade
// `defaults.ts`. Used only when the component is rendered without
// `unconnectedApps` (Storybook / standalone usage). In production the creator
// always emits a non-empty list, so this default rarely fires.
const FALLBACK_APPS: ThirdPartyApp[] = DEFAULT_FALLBACK_APPS as ThirdPartyApp[];

/**
 * Recognised cohort values for the `cc-connect-your-3p-tools-in-onboarding`
 * experiment. Any other value (including `undefined`, `''`, `'control'`,
 * `'not-enrolled'`, or unknown strings) is treated as a Statsig anomaly and
 * suppresses the banner.
 */
const COHORT_SINGLE = 'single-connect-in-onboarding';
const COHORT_MULTI = 'multi-connect-in-onboarding';

type CohortLimit = 1 | 3;

/**
 * Maps a Statsig cohort value to the number of fresh, unconnected apps the
 * banner must have to render. Returns `null` for any unrecognised value to
 * fail closed when Statsig assignment is missing or unexpected.
 */
function cohortToLimit(cohort: string | undefined): CohortLimit | null {
	switch (cohort) {
		case COHORT_SINGLE:
			return 1;
		case COHORT_MULTI:
			return 3;
		default:
			return null;
	}
}

// Maximum recommendations we ever request from `use3pConnectorResources`.
// Matches the largest cohort limit so the same hook call serves every cohort.
const MAX_CONNECTORS = 3;

export const Connect3pTools = ({
	// `action` and `onPrimaryActionClick` are intentionally accepted but
	// unused — the underscore prefix tells `oxlint` to allow this. They
	// remain part of the public `Connect3pToolsProps` API for consistency
	// with the contract every `confluence-side-nav` placement message
	// exposes (and that the placement renderer wires up automatically),
	// but invoking `onPrimaryActionClick` synchronously unmounts this
	// component via the placement's `messages.slice(1)` state update —
	// see the comment in `handleOnPrimaryActionClick` for the full story.
	action: _action,
	unconnectedApps = FALLBACK_APPS,
	cohort,
	cloudId,
	userId,
	topApps,
	department,
	numTeammates,
	appName,
	onPrimaryActionClick: _onPrimaryActionClick,
	onDismissClick,
}: Connect3pToolsProps): JSX.Element | null => {
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { messageInstanceClicked, messageInstanceViewed } = useMessageInstanceEvent();
	const [appIcons, setAppIcons] = useState<Map<ThirdPartyApp, RovoLogo>>(new Map());
	const [isModalOpen, setIsModalOpen] = useState(false);
	const intl = useIntl();

	// Look up which of the recommended apps the user has already connected so
	// we can trim them out of the banner. Without this filter the nudge can
	// keep recommending apps that the user has already authenticated against,
	// which is a poor onboarding UX (the whole point of the nudge is to drive
	// fresh connections).
	// `use3pConnectorResources` is always called (the hook signature requires
	// `cloudId`), but `cloudId` may be `undefined` in Storybook / standalone
	// usage. In that case we pass `''` — the hook still runs, initialises
	// `isLoading: true`, and falls back to its unauthenticated default list
	// (`DEFAULT_SMARTLINKS_CONNECTOR_FALLBACKS`). The component therefore
	// renders `null` during the initial async tick even in Storybook; that is
	// a known cosmetic trade-off for a single source of truth on connection
	// state in production. `onConnectorConnected` is wired below into each
	// connector's `onConnect` callback so the hook's local state updates
	// without a page reload after a successful in-modal auth — without this
	// the just-connected app would keep showing in the nudge until refresh.
	const {
		connectorResources,
		isLoading: isConnectorLoading,
		onConnectorConnected,
	} = use3pConnectorResources({
		cloudId: cloudId ?? '',
		userId,
		// In Confluence session-data, `userId` is the Atlassian Account ID —
		// the same value the outbound-auth gateway expects.
		aaId: userId,
		maxConnectors: MAX_CONNECTORS,
	});

	// Build a Set of productKeys the user has authenticated against so we can
	// filter the recommended `unconnectedApps` list down to the truly fresh
	// ones. Recomputed only when `connectorResources` identity changes.
	const connectedProductKeys = new Set(
		connectorResources.filter((r) => r.isAuthenticated).map((r) => r.productKey),
	);

	const freshApps = unconnectedApps.filter(
		(app) => !connectedProductKeys.has(APP_CONFIG[app].productKey),
	);

	const cohortLimit = cohortToLimit(cohort);
	const appsToRender = cohortLimit !== null ? freshApps.slice(0, cohortLimit) : [];

	// Has the banner actually committed to rendering on this paint? Mirrors the
	// suppression conditions below so analytics fire exactly once per user who
	// truly sees the nudge — not for the brief mounted-then-suppressed window
	// that exists for every user while `use3pConnectorResources` is loading or
	// when their connection state ultimately suppresses the banner. Without
	// this gate the "viewed" count is inflated by every mount, corrupting
	// experiment exposure metrics.
	const willRender = cohortLimit !== null && !isConnectorLoading && freshApps.length >= cohortLimit;

	// Track whether the viewed event has already fired for this mount, so we
	// don't double-fire if the component re-renders for any reason after the
	// guard transitions to true (e.g. a parent re-render or cohort prop change
	// that keeps `willRender` true).
	const hasFiredViewedRef = useRef(false);

	useEffect(() => {
		if (!willRender || hasFiredViewedRef.current) {
			return;
		}
		hasFiredViewedRef.current = true;

		if (messageInstanceViewed) {
			void messageInstanceViewed({ ...(cohort !== undefined && { cohort }) });
		}
		fireAnalyticsEvent({
			eventType: 'screen',
			name: 'ConfluenceConnect3pToolsSideNavNudge',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only re-runs when `willRender` flips
	}, [willRender]);

	// Load icons from ThirdPartyConfigs on mount / when unconnectedApps changes
	useEffect(() => {
		let cancelled = false;

		const loadIcons = async () => {
			const iconMap = new Map<ThirdPartyApp, RovoLogo>();
			for (const app of appsToRender) {
				const config = await ThirdPartyConfigs.get(APP_CONFIG[app].productKey);
				if (config && !cancelled) {
					iconMap.set(app, config.icon);
				}
			}
			if (!cancelled) {
				setAppIcons(iconMap);
			}
		};

		void loadIcons();

		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- appsToRender identity is derived; we want to re-run only when its membership changes
	}, [appsToRender.join(',')]);

	const fireMessageInstanceClicked = ({
		actionType,
		clickedElement,
	}: Connect3pToolsMessageInstanceClickedParams) => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: clickedElement,
			action: {
				actionType: actionType,
			},
			additionalMetadataFromFrontend: {
				...(cohort !== undefined && { cohort }),
			},
		});
	};

	const handleOnPrimaryActionClick = () => {
		void fireMessageInstanceClicked({
			actionType: 'open',
			clickedElement: {
				clickIdentifier: 'showMeHow',
				clickedElement: 'button',
				buttonType: 'primary',
			},
		});

		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'connect3pToolsSideNavNudgeCta',
		});

		// NOTE: do not call `onPrimaryActionClick?.(action)` here. The
		// `confluence-side-nav` placement reacts to it by slicing this
		// message out of its `messages` state (see `placement-v2/src/
		// confluence-side-nav/index.tsx` → `handleOnSideNavClick`), which
		// unmounts this component before React can flush the
		// `setIsModalOpen(true)` update — so the modal never renders.
		// The placement notification is instead deferred to
		// `handleOnModalClose`, which fires `onDismissClick?.()` once the
		// user has actually finished with the modal.
		setIsModalOpen(true);
	};

	const handleOnModalClose = () => {
		setIsModalOpen(false);
		// Dismiss the nudge after the user closes the modal — once they've
		// interacted with the primary action, we don't need to show it again.
		// This also signals the placement (via the placement's
		// `handleOnDismissed`) to remove this message from its list, which
		// is intentional: by this point the user is done with the modal so
		// unmounting the banner has no visible side effects.
		void onDismissClick?.();
	};

	const handleOnDismissClick = () => {
		void fireMessageInstanceClicked({
			actionType: 'dismiss',
			clickedElement: {
				clickIdentifier: 'dismiss',
				clickedElement: 'cross-icon',
			},
		});

		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'connect3pToolsSideNavNudgeDismiss',
		});

		void onDismissClick?.();
	};

	// Suppression rules in priority order:
	//
	// 1. Unknown / missing cohort (control, not-enrolled, '', undefined, etc.)
	//    signals a Statsig anomaly — fail closed and render nothing rather
	//    than nudge with an arbitrary default size. Without this guard we'd
	//    silently default to multi-app sizing for users who were never
	//    enrolled, which both biases experiment metrics and ships an
	//    unintended treatment to control users.
	// 2. While the connector lookup is in flight, render nothing to avoid a
	//    flash-then-vanish if the user turns out to already have enough apps
	//    connected. The hook starts with `isLoading: true` so this guard
	//    naturally covers the first render.
	// 3. If fewer fresh recommendations remain than the cohort requires, don't
	//    fall back to a smaller list — just suppress. Product decision: the
	//    single- and multi-app variants are different treatments in the
	//    experiment, so emitting a 1-app banner under the multi-app cohort
	//    would contaminate that arm. Suppress instead so the experiment
	//    remains clean.
	if (cohortLimit === null || isConnectorLoading || freshApps.length < cohortLimit) {
		return null;
	}

	const isSingleApp = appsToRender.length === 1;
	const bodyMessage = isSingleApp ? i18n.singleAppBodyLabel : i18n.multiAppBodyLabel;
	const ctaMessage = isSingleApp
		? i18n.callToActionForSingleAppLabel
		: i18n.callToActionForMultiAppLabel;
	const firstApp = appsToRender[0];
	const messageValues =
		isSingleApp && firstApp ? { appName: APP_CONFIG[firstApp].displayName } : undefined;

	const modalConnectorResources = appsToRender.flatMap((app) => {
		const productKey = APP_CONFIG[app].productKey;
		const connectorResource = connectorResources.find(
			(resource) => resource.productKey === productKey,
		);
		return connectorResource ? [connectorResource] : [];
	});

	return (
		<AnalyticsContext data={{ source: 'confluence-connect-3p-tools-nudge' }}>
			<Flex xcss={styles.root} testId="confluence-connect-3p-tools-nudge" direction="row">
				{/* Left column: icons */}
				<Flex xcss={styles.iconsColumn} alignItems="start">
					<Flex direction="row" alignItems="center">
						{appsToRender.map((app, index) => {
							const Icon = appIcons.get(app);
							if (!Icon) {
								return null;
							}
							return (
								<Box key={app} xcss={index === 0 ? styles.iconWrapper : styles.iconWrapperOverlap}>
									<Icon label="" size="small" />
								</Box>
							);
						})}
					</Flex>
				</Flex>
				{/* Right column: text content */}
				<Flex xcss={styles.primaryContentContainer} gap="space.100" direction="column">
					<Heading size="small">
						<FormattedMessage {...i18n.headingLabel} />
					</Heading>
					<Text size="small" color="color.text.subtle">
						<FormattedMessage {...bodyMessage} values={messageValues} />
					</Text>
					<Box>
						<Pressable
							xcss={styles.callToActionContainer}
							onClick={handleOnPrimaryActionClick}
							testId="connect-3p-tools-cta-button"
						>
							<Text color="color.link" size="medium">
								<FormattedMessage {...ctaMessage} values={messageValues} />
							</Text>
						</Pressable>
					</Box>
				</Flex>
				<Box xcss={styles.closeButtonContainer}>
					<IconButton
						appearance="subtle"
						spacing="compact"
						icon={(props) => <CrossIcon {...props} size="small" />}
						label={intl.formatMessage(i18n.dismissLabel)}
						isTooltipDisabled={false}
						onClick={handleOnDismissClick}
					/>
				</Box>
			</Flex>
			{numTeammates !== undefined && appName !== undefined ? (
				<Connect3pToolsModal
					isOpen={isModalOpen}
					onClose={handleOnModalClose}
					connectorResources={modalConnectorResources}
					onConnectorConnected={onConnectorConnected}
					numTeammates={numTeammates}
					appName={appName}
					topApps={topApps}
					department={department}
				/>
			) : (
				<Connect3pToolsModal
					isOpen={isModalOpen}
					onClose={handleOnModalClose}
					connectorResources={modalConnectorResources}
					onConnectorConnected={onConnectorConnected}
					topApps={topApps}
					department={department}
				/>
			)}
		</AnalyticsContext>
	);
};
